import React, { useState } from "react"
import { Polyline, Polygon, Tooltip, useMap, useMapEvents } from "react-leaflet"
import L from "leaflet"
import { MapObject, MapConnection } from "api/mapview/mapview.pb"

interface ConnectionLineProps {
    connection: MapConnection
    objects: MapObject[]
}

const ConnectionLine: React.FC<ConnectionLineProps> = ({ connection, objects }) => {
    const fromObject = objects.find(obj => obj.id === connection.fromObjectId)
    const toObject = objects.find(obj => obj.id === connection.toObjectId)
    const map = useMap()
    
    // Отслеживаем изменения зума карты
    const [zoom, setZoom] = useState(map.getZoom())
    
    useMapEvents({
        zoomend: () => {
            setZoom(map.getZoom())
        },
    })

    if (!fromObject || !toObject || !map) {
        return null
    }

    if (!fromObject.coords || !toObject.coords) {
        return null
    }

    const positions = [
        [fromObject.coords.x, fromObject.coords.y],
        [toObject.coords.x, toObject.coords.y]
    ] as L.LatLngExpression[]

    // Настройки стиля линии
    const pathOptions = {
        color: connection.color || '#3388ff',
        weight: connection.weight || 3,
        dashArray: connection.dashArray,
        opacity: 0.8,
    }

    // Создание треугольных стрелок по всей линии с учетом проекции
    const createTriangleArrows = (from: [number, number], to: [number, number]) => {
        const currentZoom = map.getZoom()
        
        // Адаптивный размер стрелок в зависимости от зума
        // При маленьком зуме (1-5) стрелки больше, при большом зуме (15+) меньше
        // После зума 10 размер стрелок не меняется
        const effectiveZoom = Math.min(currentZoom, 10) // ограничиваем зум для расчётов
        const zoomFactor = Math.max(1.5, Math.min(4.0, (9 - effectiveZoom) * 0.3 + 2.0))
        const baseArrowSize = connection.arrowSize || 18 // увеличили базовый размер
        const arrowSizePx = baseArrowSize * zoomFactor
        
        // Адаптивное расстояние между стрелками
        const baseSpacing = 80 // увеличили базовое расстояние
        const arrowSpacingPx = baseSpacing * Math.max(0.8, Math.min(2.0, zoomFactor))

        // Проецируем координаты в экранные точки
        const fromPoint = map.project(L.latLng(from[0], from[1]), currentZoom)
        const toPoint = map.project(L.latLng(to[0], to[1]), currentZoom)

        // Вектор направления
        const dx = toPoint.x - fromPoint.x
        const dy = toPoint.y - fromPoint.y
        const length = Math.sqrt(dx * dx + dy * dy)
        const angle = Math.atan2(dy, dx)

        // Минимальная длина линии для отображения стрелок (адаптивная к зуму)
        const minLength = Math.max(25, 50 / zoomFactor)
        if (length < minLength) {
            return []
        }

        // Количество стрелок - для коротких линий одна, для длинных несколько
        let arrowCount = Math.max(1, Math.floor(length / arrowSpacingPx))
        
        const triangles: L.LatLngExpression[][] = []

        for (let i = 1; i <= arrowCount; i++) {
            const progress = i / (arrowCount + 1) // равномерное распределение
            
            // Центр стрелки
            const cx = fromPoint.x + dx * progress
            const cy = fromPoint.y + dy * progress

            // Создаем маленький треугольник-стрелку
            // Вершина стрелки (направлена вперед по линии)
            const tipX = cx + Math.cos(angle) * arrowSizePx
            const tipY = cy + Math.sin(angle) * arrowSizePx

            // Основание треугольника (перпендикулярно линии)
            const baseAngle1 = angle + Math.PI * 0.75 // 135 градусов
            const baseAngle2 = angle - Math.PI * 0.75 // -135 градусов
            
            const baseX1 = cx + Math.cos(baseAngle1) * arrowSizePx * 0.9 // увеличили с 0.7 до 0.9
            const baseY1 = cy + Math.sin(baseAngle1) * arrowSizePx * 0.9
            const baseX2 = cx + Math.cos(baseAngle2) * arrowSizePx * 0.9
            const baseY2 = cy + Math.sin(baseAngle2) * arrowSizePx * 0.9

            // Обратно в LatLng
            const tipLatLng = map.unproject([tipX, tipY], currentZoom)
            const baseLatLng1 = map.unproject([baseX1, baseY1], currentZoom)
            const baseLatLng2 = map.unproject([baseX2, baseY2], currentZoom)

            triangles.push([
                [tipLatLng.lat, tipLatLng.lng],
                [baseLatLng1.lat, baseLatLng1.lng],
                [baseLatLng2.lat, baseLatLng2.lng]
            ])
        }
        return triangles
    }

    const triangleArrows = createTriangleArrows(
        [fromObject.coords.x, fromObject.coords.y], 
        [toObject.coords.x, toObject.coords.y]
    )

    return (
        <>
            {/* Основная линия */}
            <Polyline 
                positions={positions} 
                pathOptions={pathOptions}
            >
                {connection.label && (
                    <Tooltip permanent={false} direction="center">
                        {connection.label}
                    </Tooltip>
                )}
            </Polyline>
            
            {/* Треугольные стрелки вдоль линии */}
            {triangleArrows.map((triangle: L.LatLngExpression[], index: number) => (
                <Polygon 
                    key={`triangle-${index}`}
                    positions={triangle}
                    pathOptions={{
                        color: pathOptions.color,
                        fillColor: pathOptions.color,
                        fillOpacity: 0.9, // Увеличена непрозрачность для лучшей видимости
                        weight: 1,
                        opacity: 1
                    }}
                />
            ))}
        </>
    )
}

export default ConnectionLine
